import React from 'react';
import { useDrawingBoard } from './useDrawingBoard';
import { CANVAS_WIDTH, CANVAS_HEIGHT, NOTE_COLORS, NOTE_W, NOTE_H, TRANSPARENT, PathData, CANVAS_COLORS } from './types';
import { pointsToSvgPath, buildVariableStrokePath } from './utils';

export type DrawingBoardState = ReturnType<typeof useDrawingBoard>;

export default function DrawingCanvas({ board }: { board: DrawingBoardState }) {
  const {
    isFullscreen, svgCallbackRef, tool, startDrawing, draw, stopDrawing, layers, currentPath,
    textElements, editingTextId, handleTextDragStart, handleTextDragMove, handleTextDragEnd,
    draggingText, setEditingTextId, setEditingTextValue, deleteTextElement, notes, editingNoteId,
    handleNoteDragStart, handleNoteDragMove, handleNoteDragEnd, draggingNote, setEditingNoteId,
    setEditingNoteText, deleteNote, editingNoteText, confirmNoteEdit, editingTextValue, confirmTextEdit,
    gridMode, gridSize, svgRef, canvasColor
  } = board;

  const renderGrid = () => {
    if (gridMode === 'none') return null;
    const cols = Math.ceil(CANVAS_WIDTH / gridSize);
    const rows = Math.ceil(CANVAS_HEIGHT / gridSize);
    const gridColor = isFullscreen ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';
    const dotColor = isFullscreen ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)';

    if (gridMode === 'dots') {
      const dots: React.ReactElement[] = [];
      for (let r = 0; r <= rows; r++) {
        for (let c = 0; c <= cols; c++) {
          dots.push(
            <circle
              key={`d-${r}-${c}`}
              cx={c * gridSize}
              cy={r * gridSize}
              r={1.2}
              fill={dotColor}
              pointerEvents="none"
            />
          );
        }
      }
      return <g pointerEvents="none">{dots}</g>;
    }

    if (gridMode === 'cross') {
      const crosses: React.ReactElement[] = [];
      const s = 3;
      for (let r = 0; r <= rows; r++) {
        for (let c = 0; c <= cols; c++) {
          const cx = c * gridSize;
          const cy = r * gridSize;
          crosses.push(
            <g key={`cr-${r}-${c}`} pointerEvents="none">
              <line x1={cx - s} y1={cy} x2={cx + s} y2={cy} stroke={dotColor} strokeWidth={1} />
              <line x1={cx} y1={cy - s} x2={cx} y2={cy + s} stroke={dotColor} strokeWidth={1} />
            </g>
          );
        }
      }
      return <g pointerEvents="none">{crosses}</g>;
    }

    // lines mode
    const lines: React.ReactElement[] = [];
    for (let c = 0; c <= cols; c++) {
      lines.push(
        <line
          key={`v-${c}`}
          x1={c * gridSize} y1={0}
          x2={c * gridSize} y2={CANVAS_HEIGHT}
          stroke={gridColor} strokeWidth={1}
          pointerEvents="none"
        />
      );
    }
    for (let r = 0; r <= rows; r++) {
      lines.push(
        <line
          key={`h-${r}`}
          x1={0} y1={r * gridSize}
          x2={CANVAS_WIDTH} y2={r * gridSize}
          stroke={gridColor} strokeWidth={1}
          pointerEvents="none"
        />
      );
    }
    return <g pointerEvents="none">{lines}</g>;
  };

  // Render a single path/shape as React element
  const renderPathElement = (path: PathData) => {
    const { points, color, width, tool, id, fill, widths, opacity = 1 } = path;

    if (tool === 'eraser') {
      // Eraser: simple white stroke, no variable width needed
      return (
        <path
          key={id}
          d={pointsToSvgPath(points)}
          stroke="#ffffff"
          strokeWidth={width}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    }

    if (tool === 'pen') {
      // Pen with variable pressure: use filled outline polygon
      if (widths && widths.length >= 2 && points.length >= 2) {
        return (
          <path
            key={id}
            d={buildVariableStrokePath(points, widths)}
            fill={color}
            stroke="none"
            opacity={opacity}
          />
        );
      }
      // Fallback for single point or no widths data
      return (
        <path
          key={id}
          d={pointsToSvgPath(points)}
          stroke={color}
          strokeWidth={width}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={opacity}
        />
      );
    }

    if (points.length < 2) return null;

    const start = points[0];
    const end = points[points.length - 1];
    const minX = Math.min(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const w = Math.abs(end.x - start.x);
    const h = Math.abs(end.y - start.y);
    const fillValue = fill && fill !== TRANSPARENT ? fill : 'none';

    switch (tool) {
      case 'rectangle':
        return (
          <rect
            key={id}
            x={minX}
            y={minY}
            width={w}
            height={h}
            stroke={color}
            strokeWidth={width}
            fill={fillValue}
            opacity={opacity}
          />
        );
      case 'circle': {
        const cx = (start.x + end.x) / 2;
        const cy = (start.y + end.y) / 2;
        const r = Math.max(w, h) / 2;
        return (
          <circle
            key={id}
            cx={cx}
            cy={cy}
            r={r}
            stroke={color}
            strokeWidth={width}
            fill={fillValue}
            opacity={opacity}
          />
        );
      }
      case 'ellipse': {
        const ecx = (start.x + end.x) / 2;
        const ecy = (start.y + end.y) / 2;
        const rx = w / 2;
        const ry = h / 2;
        return (
          <ellipse
            key={id}
            cx={ecx}
            cy={ecy}
            rx={rx}
            ry={ry}
            stroke={color}
            strokeWidth={width}
            fill={fillValue}
            opacity={opacity}
          />
        );
      }
      case 'triangle': {
        const topX = (start.x + end.x) / 2;
        const topY = Math.min(start.y, end.y);
        const bottomY = Math.max(start.y, end.y);
        const leftX = Math.min(start.x, end.x);
        const rightX = Math.max(start.x, end.x);
        return (
          <polygon
            key={id}
            points={`${topX},${topY} ${rightX},${bottomY} ${leftX},${bottomY}`}
            stroke={color}
            strokeWidth={width}
            fill={fillValue}
            strokeLinejoin="round"
            opacity={opacity}
          />
        );
      }
      case 'line':
        return (
          <line
            key={id}
            x1={start.x}
            y1={start.y}
            x2={end.x}
            y2={end.y}
            stroke={color}
            strokeWidth={width}
            strokeLinecap="round"
            opacity={opacity}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`overflow-hidden transition-all duration-300 ${
        isFullscreen 
          ? 'flex-1 m-4 shadow-2xl ring-1 ring-white/10 rounded-lg' 
          : 'flex-1 border border-stone-200 rounded-lg'
      }`}
              style={{
                backgroundColor: CANVAS_COLORS.find(c => c.id === canvasColor)?.bg || 'white',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              } as React.CSSProperties}
            >
              <svg
                ref={svgCallbackRef}
                viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
                className={`w-full h-full ${tool === 'fill' ? 'cursor-pointer' : 'cursor-crosshair'}`}
                style={{
                  touchAction: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  WebkitTapHighlightColor: 'transparent',
                } as React.CSSProperties}
                onPointerDown={startDrawing}
                onPointerMove={draw}
                onPointerUp={stopDrawing}
                onPointerLeave={(e) => { e.preventDefault(); stopDrawing(e); }}
                onPointerCancel={(e) => { e.preventDefault(); stopDrawing(e); }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <rect 
                  width="100%" 
                  height="100%" 
                  fill={CANVAS_COLORS.find(c => c.id === canvasColor)?.bg || 'white'} 
                />
                {/* Grid background (visual only, not saved) */}
                {renderGrid()}
                {layers.map((layer) => (
                  <g
                    key={layer.id}
                    style={{
                      opacity: layer.visible ? 1 : 0,
                      pointerEvents: layer.visible ? 'auto' : 'none',
                    }}
                  >
                    {layer.paths.map((path) => renderPathElement(path))}
                  </g>
                ))}
                {/* Current drawing path (on active layer) */}
                {currentPath && (
                  <g>
                    {renderPathElement(currentPath)}
                  </g>
                )}

                {/* Text Elements Layer */}
                {textElements.map((textEl) => {
                  const isEditingThis = editingTextId === textEl.id;
                  const lines = textEl.text.split('\n');
                  return (
                    <g key={textEl.id}>
                      {/* Drag handle area */}
                      <g
                        onPointerDown={(e) => {
                          if (tool === 'text' && !isEditingThis) {
                            handleTextDragStart(e, textEl.id);
                          }
                        }}
                        onPointerMove={handleTextDragMove}
                        onPointerUp={handleTextDragEnd}
                        onPointerCancel={handleTextDragEnd}
                        style={{ cursor: draggingText?.id === textEl.id ? 'grabbing' : (tool === 'text' ? 'move' : 'default') }}
                      >
                        {/* Invisible hit area */}
                        <rect
                          x={textEl.x - 4}
                          y={textEl.y - textEl.fontSize}
                          width={Math.max(textEl.text.length * textEl.fontSize * 0.6, 40) + 8}
                          height={textEl.fontSize * 1.4 * Math.max(lines.length, 1) + 8}
                          fill="transparent"
                          stroke={tool === 'text' ? '#94a3b8' : 'none'}
                          strokeWidth={1}
                          strokeDasharray="3 2"
                          rx={3}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            setEditingTextId(textEl.id);
                            setEditingTextValue(textEl.text);
                          }}
                        />
                        {/* Text content */}
                        {!isEditingThis && (
                          <text
                            x={textEl.x}
                            y={textEl.y}
                            fontSize={textEl.fontSize}
                            fill={textEl.color}
                            fontFamily={textEl.fontFamily}
                            pointerEvents="none"
                          >
                            {lines.map((line, i) => (
                              <tspan key={i} x={textEl.x} dy={i === 0 ? 0 : textEl.fontSize * 1.3}>
                                {line || ' '}
                              </tspan>
                            ))}
                          </text>
                        )}
                        {/* Delete button (only in text tool mode) */}
                        {tool === 'text' && !isEditingThis && (
                          <g
                            onPointerDown={(e) => { e.stopPropagation(); deleteTextElement(textEl.id); }}
                            style={{ cursor: 'pointer' }}
                          >
                            <circle
                              cx={textEl.x + Math.max(textEl.text.length * textEl.fontSize * 0.6, 40) + 10}
                              cy={textEl.y - textEl.fontSize + 6}
                              r={7}
                              fill="#ef4444"
                              opacity={0.85}
                            />
                            <text
                              x={textEl.x + Math.max(textEl.text.length * textEl.fontSize * 0.6, 40) + 10}
                              y={textEl.y - textEl.fontSize + 10}
                              fontSize={10}
                              fill="white"
                              textAnchor="middle"
                              fontFamily="sans-serif"
                              pointerEvents="none"
                            >×</text>
                          </g>
                        )}
                      </g>
                    </g>
                  );
                })}

                {/* Sticky Notes Layer */}
                {notes.map((note) => {
                  const nc = NOTE_COLORS[note.colorIndex] || NOTE_COLORS[0];
                  const isEditing = editingNoteId === note.id;
                  return (
                    <g key={note.id}>
                      {/* Connector line */}
                      <line
                        x1={note.anchorX}
                        y1={note.anchorY}
                        x2={note.noteX + NOTE_W / 2}
                        y2={note.noteY + NOTE_H}
                        stroke={nc.border}
                        strokeWidth={1.5}
                        strokeDasharray="5 3"
                        pointerEvents="none"
                      />
                      {/* Anchor dot */}
                      <circle
                        cx={note.anchorX}
                        cy={note.anchorY}
                        r={4}
                        fill={nc.border}
                        pointerEvents="none"
                      />
                      {/* Note body - draggable */}
                      <g
                        onPointerDown={(e) => handleNoteDragStart(e, note.id)}
                        onPointerMove={handleNoteDragMove}
                        onPointerUp={handleNoteDragEnd}
                        onPointerCancel={handleNoteDragEnd}
                        style={{ cursor: draggingNote?.id === note.id ? 'grabbing' : 'grab' }}
                      >
                        {/* Shadow */}
                        <rect
                          x={note.noteX + 3}
                          y={note.noteY + 3}
                          width={NOTE_W}
                          height={NOTE_H}
                          rx={6}
                          fill="rgba(0,0,0,0.10)"
                          pointerEvents="none"
                        />
                        {/* Note background */}
                        <rect
                          x={note.noteX}
                          y={note.noteY}
                          width={NOTE_W}
                          height={NOTE_H}
                          rx={6}
                          fill={nc.bg}
                          stroke={nc.border}
                          strokeWidth={1.5}
                        />
                        {/* Top bar */}
                        <rect
                          x={note.noteX}
                          y={note.noteY}
                          width={NOTE_W}
                          height={20}
                          rx={6}
                          fill={nc.border}
                          opacity={0.5}
                          pointerEvents="none"
                        />
                        <rect
                          x={note.noteX}
                          y={note.noteY + 14}
                          width={NOTE_W}
                          height={6}
                          fill={nc.border}
                          opacity={0.5}
                          pointerEvents="none"
                        />
                        {/* Note text */}
                        {!isEditing && (
                          <text
                            x={note.noteX + 8}
                            y={note.noteY + 30}
                            fontSize={11}
                            fill="#1c1917"
                            fontFamily="sans-serif"
                            pointerEvents="none"
                          >
                            {note.text.split('\n').map((line, i) => (
                              <tspan key={i} x={note.noteX + 8} dy={i === 0 ? 0 : 14}>{line || ' '}</tspan>
                            ))}
                          </text>
                        )}
                        {/* Edit hint when empty */}
                        {!isEditing && !note.text && (
                          <text
                            x={note.noteX + 8}
                            y={note.noteY + 34}
                            fontSize={10}
                            fill={nc.border}
                            fontFamily="sans-serif"
                            pointerEvents="none"
                            opacity={0.8}
                          >
                            Çift tıkla düzenle...
                          </text>
                        )}
                        {/* Double-click to edit */}
                        <rect
                          x={note.noteX}
                          y={note.noteY + 20}
                          width={NOTE_W}
                          height={NOTE_H - 20}
                          fill="transparent"
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            setEditingNoteId(note.id);
                            setEditingNoteText(note.text);
                          }}
                          style={{ cursor: 'text' }}
                        />
                        {/* Delete button */}
                        <g
                          onPointerDown={(e) => { e.stopPropagation(); deleteNote(note.id); }}
                          style={{ cursor: 'pointer' }}
                        >
                          <circle cx={note.noteX + NOTE_W - 8} cy={note.noteY + 10} r={7} fill={nc.border} opacity={0.8} />
                          <text x={note.noteX + NOTE_W - 8} y={note.noteY + 14} fontSize={10} fill="white" textAnchor="middle" fontFamily="sans-serif" pointerEvents="none">×</text>
                        </g>
                      </g>
                    </g>
                  );
                })}
              </svg>

              {/* Floating textarea for note editing (HTML overlay) */}
              {editingNoteId && (() => {
                const note = notes.find((n) => n.id === editingNoteId);
                if (!note) return null;
                const svg = svgRef.current;
                if (!svg) return null;
                const rect = svg.getBoundingClientRect();
                const scaleX = rect.width / CANVAS_WIDTH;
                const scaleY = rect.height / CANVAS_HEIGHT;
                const left = rect.left + note.noteX * scaleX;
                const top = rect.top + (note.noteY + 20) * scaleY;
                const width = NOTE_W * scaleX;
                const height = (NOTE_H - 20) * scaleY;
                return (
                  <textarea
                    autoFocus
                    value={editingNoteText}
                    onChange={(e) => setEditingNoteText(e.target.value)}
                    onBlur={confirmNoteEdit}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Escape') confirmNoteEdit();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    style={{
                      position: 'fixed',
                      left,
                      top,
                      width,
                      height,
                      fontSize: 11 * scaleX,
                      padding: '4px 8px',
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      resize: 'none',
                      fontFamily: 'sans-serif',
                      color: '#1c1917',
                      zIndex: 9999,
                      lineHeight: '1.4',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    } as React.CSSProperties}
                  />
                );
              })()}

              {/* Floating textarea for text element editing (HTML overlay) */}
              {editingTextId && (() => {
                const textEl = textElements.find((t) => t.id === editingTextId);
                if (!textEl) return null;
                const svg = svgRef.current;
                if (!svg) return null;
                const rect = svg.getBoundingClientRect();
                const scaleX = rect.width / CANVAS_WIDTH;
                const scaleY = rect.height / CANVAS_HEIGHT;
                const left = rect.left + textEl.x * scaleX;
                const top = rect.top + (textEl.y - textEl.fontSize) * scaleY;
                const minWidth = 120 * scaleX;
                const fontSize = textEl.fontSize * scaleX;
                return (
                  <textarea
                    autoFocus
                    value={editingTextValue}
                    onChange={(e) => setEditingTextValue(e.target.value)}
                    onBlur={confirmTextEdit}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Escape') confirmTextEdit();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    placeholder="Yazı yaz..."
                    style={{
                      position: 'fixed',
                      left,
                      top,
                      minWidth,
                      width: 'auto',
                      minHeight: fontSize * 1.6,
                      fontSize,
                      padding: '2px 4px',
                      background: 'rgba(255,255,255,0.85)',
                      border: `2px solid ${textEl.color}`,
                      borderRadius: 4,
                      outline: 'none',
                      resize: 'none',
                      fontFamily: textEl.fontFamily,
                      color: textEl.color,
                      zIndex: 9999,
                      lineHeight: '1.3',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      overflow: 'hidden',
                    } as React.CSSProperties}
                    rows={Math.max(editingTextValue.split('\n').length, 1)}
                  />
                );
              })()}
            </div>

  );
}
